import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, EnvelopeSimple, CaretDown, CaretUp,
  MagnifyingGlass, X as XIcon,
} from "@phosphor-icons/react";
import ContactForm from "@/components/ContactForm";

// iter v29 r249 — Pagina Supporto standalone richiesta da Apple App Store
// iter v29 r261 — Form di contatto sopra l'email (anti-scraping).
// iter v29 r267 — FAQ espanse in 7 sezioni navigabili + ricerca testuale.
// Strategia: rispondere prima alle domande che generano email/ticket, così
// il form resta per i casi reali e non per "quanto costa" / "manda WhatsApp"
// che sono nel 70% delle richieste storiche.

const SUPPORT_EMAIL = "supporto@prontoimmo.it";

const SECTIONS = [
  // ─────────────────────────────────────────────────────────────────
  {
    id: "pagamenti",
    title: "Pagamenti, prezzi e fatturazione",
    blurb: "Piani, crediti, prova gratuita, fattura B2B, disdetta.",
    items: [
      {
        q: "Quanto costa Pronto?",
        a: "Tre piani. Free: gratis per sempre (10 crediti al mese, 1 immobile). Start: €29/mese o €249/anno (150 crediti, 5 immobili). Pro: €49/mese o €399/anno (320 crediti, immobili illimitati, niente watermark). Importi finali, senza applicazione IVA (regime forfettario art. 1 c. 54-89 L. 190/2014). Durante la promo di lancio (durante la promo di lancio) ogni nuovo iscritto riceve 60 crediti gratuiti una tantum. Dettagli su /pricing.",
      },
      {
        q: "C'è una prova gratuita?",
        a: "Sì. Il piano Free è gratuito a vita: 10 crediti al mese, 1 immobile, foto migliorate base. Nessuna carta richiesta. Durante la promo di lancio (durante la promo di lancio) i nuovi iscritti ricevono in aggiunta 60 crediti gratuiti una tantum, per provare tutte le funzioni. Terminata la promo, il piano Free continua con i normali 10 crediti/mese e i crediti promo residui restano come credito bonus (non scadono). Quando vuoi passare a Start/Pro lo decidi tu, senza rinnovo automatico nascosto.",
      },
      {
        q: "Cosa sono i crediti?",
        a: "I crediti servono per le elaborazioni AI che hanno un costo: 1 credito = 1 foto migliorata · 2 crediti = 1 sopralluogo vocale finalizzato (trascrizione + estrazione dati + bozza annuncio) · 3 crediti = pianta arredata o svuotamento stanza · 5 crediti = arredamento virtuale (virtual staging), reel video, o controllo qualità audio. Ogni piano include un budget mensile; il residuo è sempre visibile nella sezione Crediti. Puoi comprare top-up una tantum da €5 / 20 crediti che non scadono finché il piano resta attivo.",
      },
      {
        q: "Come posso passare da un piano all'altro?",
        a: "Dal Profilo → Abbonamento. Upgrade immediato (paghi la differenza pro-rata per il periodo rimanente). Downgrade alla scadenza del ciclo corrente. I crediti accumulati restano.",
      },
      {
        q: "Posso disdire l'abbonamento in qualsiasi momento?",
        a: "Sì. Dal Profilo → Abbonamento c'è il pulsante Disdici rinnovo. Il piano resta attivo fino alla data di scadenza già pagata, poi l'account torna automaticamente al piano Free. Non perdi gli annunci né i contatti.",
      },
      {
        q: "Quali metodi di pagamento accettate?",
        a: "Carte di credito/debito (Visa, MasterCard, American Express) tramite Stripe, infrastruttura sicura usata anche dalle banche. SEPA Direct Debit (RID) per i piani annuali. Apple Pay/Google Pay disponibili sul browser supportato.",
      },
      {
        q: "Ricevo fattura elettronica? Come va per P.IVA, codice SDI o PEC?",
        a: "Sì. In Profilo → Dati di fatturazione puoi inserire Ragione sociale, P.IVA (11 cifre), Codice SDI (7 caratteri) o PEC. Dopo il primo pagamento riceverai la fattura elettronica al tuo cassetto fiscale entro 12 giorni lavorativi, come da normativa italiana.",
      },
      {
        q: "Ho diritto al recesso di 14 giorni?",
        a: "Per i servizi digitali a esecuzione immediata (codice del consumo art. 59 lett. o), al checkout devi accettare l'avvio immediato del servizio e la rinuncia al diritto di recesso. Una volta acquistato e usato (anche un solo credito), il piano non è rimborsabile. Resta sempre attiva la prova gratuita a tempo indeterminato per testarlo prima.",
      },
      {
        q: "Cosa succede se il pagamento fallisce al rinnovo?",
        a: "Ti mandiamo 3 email a distanza di 3 giorni (giorno 0, +3, +7). Hai 14 giorni per aggiornare il metodo di pagamento prima che l'account scenda automaticamente al piano Free. I dati non vengono mai cancellati: appena risistemi la carta, riparti con tutto al posto giusto.",
      },
    ],
  },

  // ─────────────────────────────────────────────────────────────────
  {
    id: "foto-sopralluogo",
    title: "Foto migliorate, sopralluogo, PDF stampabile",
    blurb: "Cosa fa l'elaborazione automatica, in pratica.",
    items: [
      {
        q: "Come funziona il miglioramento foto?",
        a: "Carichi le foto fatte col telefono (anche venute storte o controluce). Pronto in 8 secondi sistema esposizione, prospettiva, raddrizza linee verticali, schiarisce gli ambienti scuri e ripulisce piccoli disturbi. Il risultato è una foto da listing portali, senza dover usare Lightroom o pagare un fotografo. Si vede sempre il confronto prima/dopo prima di salvare.",
      },
      {
        q: "Posso usare le foto di Pronto sui portali (Immobiliare, Idealista...)?",
        a: "Sì, sono tue. Le scarichi in alta risoluzione (fino a 4096 px lato lungo) e le carichi dove vuoi. Pronto non rivendica copyright sulle immagini elaborate.",
      },
      {
        q: "C'è il watermark \"prontoimmo.it\"?",
        a: "Sul piano Free e Start sì, in basso a destra, discreto (logo Pronto + dominio). Sul piano Pro il watermark è disattivato di default. Puoi sempre togliere/personalizzare il watermark dalle Impostazioni nel Profilo (Pro+).",
      },
      {
        q: "Il sopralluogo vocale consuma crediti? Quanti?",
        a: "Sì, 2 crediti per ogni sopralluogo finalizzato (quando premi \"Finalizza\" e ricevi la bozza annuncio + i dati strutturati + la valutazione). Iniziare un sopralluogo e abbandonarlo a metà non costa nulla, scali solo se completi. Su Start (150 crediti/mese) puoi finalizzare fino a 75 sopralluoghi al mese · Pro (320 crediti) fino a 160. La registrazione e la trascrizione progressiva durante la visita sono incluse, non c'è un costo per stanza.",
      },
      {
        q: "Come funziona il sopralluogo vocale?",
        a: "Apri Sopralluogo Live dal menu, premi il microfono e cammini per la casa raccontando cosa vedi. La voce viene trascritta in tempo reale; ogni 30 secondi Pronto pulisce refusi e nomi di vie. Scatti le foto da dentro l'app. Quando esci dalla visita hai: bozza annuncio (titolo, descrizione, dati strutturati), valore stimato di mercato, e le foto pronte nel tab Foto — quando vuoi migliorarle basta un tocco su \"Migliora tutte\".",
      },
      {
        q: "Il sopralluogo vocale funziona offline?",
        a: "La registrazione audio sì (resta sul telefono). La trascrizione e l'elaborazione richiedono connessione. Se sei senza rete, la nota viene salvata e processata appena torni online — non perdi nulla.",
      },
      {
        q: "Pronto stampa anche la scheda cartacea?",
        a: "Genera la PDF stampabile A4 pronta per la stampa (alta risoluzione, marginature corrette). La stampi tu in tipografia o sulla tua stampante. Il volantino A5 per le bacheche di condominio è incluso nei piani Pro+.",
      },
      {
        q: "Cos'è l'arredamento virtuale (virtual staging)?",
        a: "Carichi una foto di stanza vuota e Pronto ti arreda virtualmente in 3-4 varianti di stile (moderno, classico, minimal, lusso). Costa 5 crediti per foto. Utile per immobili da ristrutturare o da affittare arredati. La foto generata mostra una nota di trasparenza \"Arredamento virtuale\" come prescritto da AGCM per evitare pubblicità ingannevole.",
      },
    ],
  },

  // ─────────────────────────────────────────────────────────────────
  {
    id: "annunci-lead",
    title: "Annunci, lead, contatti, CRM",
    blurb: "Come arrivano i clienti e dove finiscono.",
    items: [
      {
        q: "Dove finiscono gli annunci che pubblico su Pronto?",
        a: "Sulla tua pagina pubblica Pronto (prontoimmo.it/agente/<tuo-nome>/annuncio/<id>), già indicizzata su Google. Le foto e la descrizione sono pronte per essere copiate sui portali (Immobiliare, Idealista, Casa.it). Pronto non pubblica automaticamente sui portali esterni: tu mantieni il controllo del prezzo e della comunicazione.",
      },
      {
        q: "Come arrivano i lead?",
        a: "Ogni pagina annuncio ha un form richiesta visita e un calcolatore mutuo. Quando un visitatore compila qualcosa, ti arriva email + push notifica + il lead va dritto nel CRM (sezione Contatti). Per i lead caldi (mutuo simulato + visita richiesta) ti suggeriamo anche un primo messaggio WhatsApp da copiare.",
      },
      {
        q: "C'è un CRM dentro Pronto?",
        a: "Sì, sezione Contatti. Ogni contatto ha: zone preferite, budget, must-have, ultimo messaggio scambiato, indice \"caldo/freddo\" automatico (heat score), promemoria di follow-up. Ti dice anche chi stai trascurando da più di 10 giorni e dovresti richiamare.",
      },
      {
        q: "Posso importare i contatti dal mio gestionale (ImmobiliareGestionale, Domus, etc.)?",
        a: "Sì, da Contatti → Importa carichi un CSV (anche esportato dal vecchio gestionale o da Excel). Mappiamo automaticamente nome/email/telefono/zone. Importazione gratuita fino a 5.000 contatti.",
      },
      {
        q: "Posso esportare tutti i miei dati?",
        a: "Sì, in qualsiasi momento. Da Profilo → Privacy → Esporta dati scarichi un ZIP con: annunci (JSON + foto originali), contatti (CSV), lead, sopralluoghi (testo + audio), fatture. È un diritto GDPR e per noi è gratis e immediato.",
      },
    ],
  },

  // ─────────────────────────────────────────────────────────────────
  {
    id: "social",
    title: "Social · Instagram, Facebook, post automatici",
    blurb: "Post, caroselli, reel pronti senza Canva.",
    items: [
      {
        q: "Pronto pubblica direttamente sui social al posto mio?",
        a: "No, è una scelta volontaria. Pronto genera il visual + la caption pronta da copia-incolla; tu pubblichi quando vuoi tu, scegli orario e hashtag locali. Niente Meta Business API, niente rischi di sospensione account, niente delega di accesso ai tuoi profili.",
      },
      {
        q: "Posso programmare i post in anticipo?",
        a: "Pronto ti genera il post + la caption + ti dice quando pubblicare (orari migliori per la tua zona). La programmazione vera dentro Instagram la fai con Meta Business Suite (gratis) collegando il tuo profilo Business. Pronto ti suggerisce calendario + frequenza ottimale.",
      },
      {
        q: "Pronto genera anche reel video?",
        a: "Sì, dal piano Pro in su. Da un set di foto di un immobile, Pronto monta un reel verticale 9:16 di 12-18 secondi con testo sovrimpresso (prezzo, zona, mq), pronto per aggiungere la musica dallo store di Instagram o TikTok al momento del post. Costa 5 crediti per reel.",
      },
      {
        q: "Mi serve un account Instagram Business per usare Pronto?",
        a: "No, basta un profilo personale. Però ti consigliamo di convertire il profilo in Business (gratis, 2 minuti) per avere le statistiche di copertura e accedere alla programmazione Meta. Su /aiuto trovi la guida passo-passo.",
      },
    ],
  },

  // ─────────────────────────────────────────────────────────────────
  // ⭐ Sezione chiave: notifiche + chiarimento WhatsApp automatico
  {
    id: "notifiche-whatsapp",
    title: "Notifiche, push, WhatsApp",
    blurb: "Cosa arriva in automatico, cosa no, e come disattivare.",
    items: [
      {
        q: "Le notifiche arrivano anche se l'app è chiusa?",
        a: "Sì, sia da computer (browser) che da iPhone (app nativa). Su browser sono Web Push standard: appena dai il consenso \"Consenti notifiche\", Pronto può inviare anche a Chrome/Safari/Firefox chiusi. Su iPhone funzionano come ogni altra app, tramite Apple Push Notification Service.",
      },
      {
        q: "Pronto manda WhatsApp automatici ai miei clienti?",
        a: "No. Pronto NON manda WhatsApp in autonomia ai tuoi contatti, mai. Quello che fa è prepararti il messaggio (testo già scritto, personalizzato per ciascun contatto) e aprire WhatsApp sul tuo telefono con il messaggio dentro, già pronto: tu premi solo Invia. Questa scelta è voluta: è l'unico modo conforme al GDPR italiano e l'unico che non rischia il ban del tuo numero WhatsApp (Meta sospende i numeri che mandano >50 messaggi automatici al giorno).",
      },
      {
        q: "Mi avvisa per festività e compleanni dei clienti?",
        a: "Sì. Ogni mattina alle 08:15 ricevi una push se ci sono: festività entro 2 giorni (Natale, Pasqua, Ferragosto, ecc.), compleanni clienti entro 2 giorni (richiede che tu abbia compilato il campo Data di nascita sul contatto). Se decidi di mandare gli auguri WhatsApp, Pronto ti prepara il testo personalizzato e tu lo invii con un tap. Mai nulla parte senza che tu lo decida.",
      },
      {
        q: "Come disattivo le notifiche?",
        a: "Da Profilo → Notifiche trovi 3 interruttori: 1) Master switch notifiche browser (on/off); 2) Notifiche agenda (briefing mattutino + promemoria appuntamenti); 3) Avvisi giornalieri (festività + compleanni clienti). Puoi anche scegliere quanto tempo prima ricevere il promemoria di un appuntamento (5, 15, 30, 60, 120 minuti).",
      },
      {
        q: "Quali notifiche manda Pronto in automatico?",
        a: "Cinque: 08:00 briefing di oggi (\"Hai 3 appuntamenti\"); 08:15 avvisi del giorno (festività, compleanni); promemoria evento 30 min prima (configurabile); 18:00 nudge serale se hai cose ancora aperte; quando arriva un lead nuovo da una pagina annuncio. Tutto disattivabile singolarmente.",
      },
      {
        q: "Funziona anche da desktop fisso senza app installata?",
        a: "Sì. Le notifiche browser (Web Push) funzionano su qualsiasi computer (Mac, Windows, Linux) usando Chrome, Edge, Firefox o Safari. La prima volta che apri Pronto da quel browser, ti chiede il permesso \"Vuoi ricevere notifiche?\" → clicchi Consenti e da quel momento ricevi tutto anche col browser chiuso (basta che il computer sia acceso e connesso).",
      },
    ],
  },

  // ─────────────────────────────────────────────────────────────────
  {
    id: "account-privacy",
    title: "Account, privacy, sicurezza dati",
    blurb: "Come funziona il login, dove finiscono le tue foto, come cancellare.",
    items: [
      {
        q: "Le mie foto vengono usate per addestrare l'intelligenza artificiale?",
        a: "No, mai. Le foto inviate per il miglioramento o l'arredamento virtuale NON vengono utilizzate dai provider esterni per addestrare i loro modelli (opt-out esplicito a contratto). Le foto restano sui nostri server crittografati e vengono cancellate al massimo 30 giorni dopo la chiusura dell'account.",
      },
      {
        q: "Come elimino il mio account e tutti i dati?",
        a: "Da Profilo → Privacy → Cancella account. La cancellazione è immediata: annunci, foto, contatti, lead, sopralluoghi vengono eliminati subito. I dati di fatturazione restano archiviati 10 anni come da obbligo fiscale italiano (DPR 600/73). Riceverai una email di conferma.",
      },
      {
        q: "Pronto funziona su iPhone? Android?",
        a: "Su iPhone è disponibile come app nativa su App Store (cerca \"Pronto Immobiliare\"). Su Android al momento si usa da browser: Chrome → menu \"Aggiungi alla schermata Home\" → la trasforma in app installabile con notifiche push. L'app Android nativa è prevista nel Q3 2026.",
      },
      {
        q: "Posso usare Pronto su più dispositivi contemporaneamente?",
        a: "Sì. Stesso account su computer, iPhone, iPad: tutto sincronizzato. Le notifiche push arrivano su tutti i dispositivi attivi. I sopralluoghi iniziati su iPhone li finisci sul computer e viceversa.",
      },
      {
        q: "Avete un account demo per provare prima di registrarmi?",
        a: "Sì. Vai su /demo e accedi all'account dimostrativo (read-only) con 3 immobili di esempio già caricati. Puoi navigare tutte le funzioni, ma non puoi modificare i dati di esempio. Per provare il flusso completo, registrati al piano Free gratuito.",
      },
      {
        q: "Cosa succede ai miei dati se cambio agenzia o smetto di fare l'agente?",
        a: "I tuoi dati restano tuoi. Puoi esportare tutto (Profilo → Esporta dati) o trasferire l'account alla nuova agenzia (Profilo → Cambia agenzia). Se chiudi l'account, hai 30 giorni di ripensamento per riattivarlo gratis; dopo, cancellazione definitiva.",
      },
    ],
  },

  // ─────────────────────────────────────────────────────────────────
  {
    id: "estensione-pubblica",
    title: "Estensione Chrome, Pronto Stima, pagine pubbliche",
    blurb: "Strumenti pubblici che convertono lead senza che tu faccia nulla.",
    items: [
      {
        q: "Cos'è l'estensione Chrome di Pronto?",
        a: "Un'estensione gratuita per il browser (Chrome/Edge su computer) che, mentre navighi su Immobiliare, Idealista, Casa.it, ti mostra accanto a ogni annuncio una stima reale di mercato (basata su OMI + comparables) e ti suggerisce se il prezzo è alto/giusto/affare. Utile per valutare al volo immobili da proporre ai clienti. Si scarica da prontoimmo.it/estensione — dal browser del computer, perché su iPhone/iPad le estensioni non sono supportate.",
      },
      {
        q: "Cos'è la mia pagina pubblica di stima (Pronto Stima)?",
        a: "Una landing page con il tuo nome (esempio: prontoimmo.it/p/mario-rossi) dove i proprietari della tua zona possono chiedere una valutazione gratuita della loro casa. Inserisci nome, foto, telefono, presentazione, e copi il link nei tuoi profili social/biglietti da visita. Ogni richiesta arriva in CRM con tutti i dati: indirizzo, mq, motivo della stima.",
      },
      {
        q: "Posso personalizzare colori e URL della pagina pubblica?",
        a: "Sì. URL: scegli tu il \"sotto-percorso\" (es. /p/mario-rossi o /p/agenzia-bergamo). Colori: dai piani Pro+ scegli il colore primario e secondario coerenti col tuo brand. Logo: carichi il tuo (PNG sfondo trasparente, min 320×320). Foto profilo: la tua, max 5 MB.",
      },
      {
        q: "Come funziona la stima automatica \"Quanto vale casa mia\"?",
        a: "Pronto incrocia: dati OMI (Osservatorio del Mercato Immobiliare, Agenzia delle Entrate, ufficiali), comparables degli ultimi 12 mesi della stessa zona/tipologia, fattori specifici (piano, esposizione, stato, anno costruzione, vista, condominio). Restituisce un valore minimo-mediano-massimo + grafico dell'andamento del quartiere. È una stima orientativa: per un valore peritale serve un sopralluogo.",
      },
    ],
  },
];

// Flatten per ricerca testuale
const FLAT = SECTIONS.flatMap((s) =>
  s.items.map((it, i) => ({ ...it, section: s.title, sectionId: s.id, idx: `${s.id}-${i}` })),
);

function FaqRow({ item, idx }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="border border-[#E5E7E0] rounded-md overflow-hidden bg-white"
      data-testid={`faq-row-${idx}`}
    >
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full px-5 py-4 flex items-center justify-between gap-3 text-left hover:bg-[#FAF8F4] transition-colors"
        data-testid={`faq-toggle-${idx}`}
        aria-expanded={open}
      >
        <span className="font-editorial text-[17px] text-[#1C1C1A] leading-tight">
          {item.q}
        </span>
        {open ? (
          <CaretUp size={16} weight="bold" className="text-[#7C8B6E] shrink-0" />
        ) : (
          <CaretDown size={16} weight="bold" className="text-[#7C8B6E] shrink-0" />
        )}
      </button>
      {open && (
        <div
          className="px-5 pb-5 text-[15px] text-[#3F3D38] leading-relaxed border-t border-[#EDEAE2] pt-4"
          data-testid={`faq-answer-${idx}`}
        >
          {item.a}
        </div>
      )}
    </div>
  );
}

function SectionBlock({ section }) {
  return (
    <section className="space-y-3" data-testid={`faq-section-${section.id}`}>
      <header className="pt-2">
        <h3 className="font-editorial text-[22px] text-[#1C1C1A] leading-tight">{section.title}</h3>
        <p className="mt-1 text-[13.5px] text-[#6B6A65]">{section.blurb}</p>
      </header>
      <div className="space-y-2">
        {section.items.map((it, i) => (
          <FaqRow key={i} item={it} idx={`${section.id}-${i}`} />
        ))}
      </div>
    </section>
  );
}

function SearchResults({ query }) {
  const q = query.trim().toLowerCase();
  const results = useMemo(() => {
    if (q.length < 2) return [];
    return FLAT.filter(
      (f) =>
        f.q.toLowerCase().includes(q) ||
        f.a.toLowerCase().includes(q) ||
        f.section.toLowerCase().includes(q),
    ).slice(0, 12);
  }, [q]);

  if (q.length < 2) return null;

  if (results.length === 0) {
    return (
      <div className="mt-6 p-5 border border-[#E5E7E0] rounded-md bg-[#FAF8F4] text-[14px] text-[#6B6A65]" data-testid="faq-search-empty">
        Nessun risultato per <strong className="text-[#1C1C1A]">&ldquo;{query}&rdquo;</strong>.
        Prova con parole diverse o scrivici tramite il form in alto.
      </div>
    );
  }

  return (
    <div className="mt-6 space-y-2" data-testid="faq-search-results">
      <div className="text-[11px] uppercase tracking-[0.18em] text-[#6B6A65] font-semibold mb-1">
        {results.length} risultat{results.length === 1 ? "o" : "i"} per &ldquo;{query}&rdquo;
      </div>
      {results.map((r) => (
        <FaqRow key={r.idx} item={r} idx={`search-${r.idx}`} />
      ))}
    </div>
  );
}

export default function Supporto() {
  const [query, setQuery] = useState("");
  const isSearching = query.trim().length >= 2;

  return (
    <div className="min-h-screen bg-[#FFFFFF]" data-testid="supporto-page">
      <header className="glass-header sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 sm:h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="brand-link-supporto">
            <img
              src="/logo/full-with-tagline.png?v=2"
              alt="Pronto · per agenti immobiliari"
              className="h-12 sm:h-14 w-auto select-none"
              draggable="false"
            />
          </Link>
          <Link
            to="/"
            className="text-sm text-[#6B6A65] hover:text-[#7C8B6E] flex items-center gap-1.5"
            data-testid="supporto-home-link"
          >
            <ArrowLeft size={14} /> Home
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 lg:px-10 py-12 lg:py-20">
        <div className="text-[11px] uppercase tracking-[0.22em] text-[#7C8B6E] font-semibold mb-3">
          Supporto
        </div>
        <h1
          className="font-editorial text-4xl lg:text-5xl tracking-tight"
          data-testid="supporto-title"
        >
          Ciao, in cosa possiamo aiutarti?
        </h1>
        <p className="mt-5 text-[17px] text-[#3F3D38] leading-relaxed max-w-2xl">
          Rispondiamo a ogni messaggio entro 24 ore lavorative, dal lunedì al venerdì.
          Per le domande più frequenti, la risposta è già qui sotto.
        </p>

        {/* Form contatto — canale primario */}
        <div className="mt-10" data-testid="supporto-contact-form">
          <ContactForm defaultSubject="generale" hideTitle />
        </div>

        {/* Email visibile in basso (Apple Review + GDPR §15-22) */}
        <div className="mt-8 bg-[#F1F4ED] border border-[#D5DAC9] rounded-lg p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="w-10 h-10 rounded-full bg-[#7C8B6E] text-white flex items-center justify-center shrink-0">
            <EnvelopeSimple size={18} weight="regular" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[11px] uppercase tracking-[0.18em] text-[#5A6A4D] font-semibold mb-0.5">
              Preferisci scriverci direttamente?
            </div>
            <a
              href={`mailto:${SUPPORT_EMAIL}`}
              className="font-editorial text-lg text-[#1C1C1A] hover:text-[#7C8B6E] transition-colors break-all"
              data-testid="supporto-email-link"
            >
              {SUPPORT_EMAIL}
            </a>
            <p className="mt-1 text-[12px] text-[#6B6A65] leading-snug">
              Email diretta, per chi preferisce il proprio client di posta.
            </p>
          </div>
        </div>

        {/* FAQ */}
        <section className="mt-14" data-testid="supporto-faq">
          <h2 className="font-editorial text-2xl lg:text-3xl text-[#1C1C1A]">
            Domande frequenti
          </h2>
          <p className="mt-2 text-[14px] text-[#6B6A65]">
            {SECTIONS.reduce((acc, s) => acc + s.items.length, 0)} risposte organizzate in {SECTIONS.length} sezioni. Cerca o sfoglia.
          </p>

          {/* Search box */}
          <div className="mt-6 relative">
            <MagnifyingGlass size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#9A968B]" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder='Cerca · es. "watermark", "cancellare account", "P.IVA"'
              className="w-full bg-white border border-[#E5E7E0] rounded-md pl-11 pr-10 py-3 text-[14.5px] text-[#1C1C1A] focus:outline-none focus:border-[#7C8B6E] focus:ring-1 focus:ring-[#7C8B6E]"
              data-testid="faq-search-input"
              aria-label="Cerca nelle domande frequenti"
            />
            {query && (
              <button
                onClick={() => setQuery("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-[#6B6A65] hover:text-[#1C1C1A]"
                data-testid="faq-search-clear"
                aria-label="Pulisci ricerca"
              >
                <XIcon size={14} weight="bold" />
              </button>
            )}
          </div>

          {/* Risultati ricerca o sezioni complete */}
          {isSearching ? (
            <SearchResults query={query} />
          ) : (
            <div className="mt-8 space-y-10">
              {SECTIONS.map((s) => (
                <SectionBlock key={s.id} section={s} />
              ))}
            </div>
          )}
        </section>

        {/* Footer info */}
        <div className="mt-16 pt-6 border-t border-[#E5E7E0]">
          <p className="text-xs text-[#6B6A65]">
            Pronto · per agenti immobiliari · prontoimmo.it · {SUPPORT_EMAIL}
          </p>
          <div className="mt-4 flex flex-wrap gap-3 text-xs">
            <Link to="/privacy" className="text-[#7C8B6E] hover:underline">
              Informativa Privacy
            </Link>
            <span className="text-[#D5DAC9]">·</span>
            <Link to="/terms" className="text-[#7C8B6E] hover:underline">
              Termini e Condizioni
            </Link>
            <span className="text-[#D5DAC9]">·</span>
            <Link to="/pricing" className="text-[#7C8B6E] hover:underline">
              Piani e prezzi
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}
